<template>
  <div class="quality-alerts-section">
    <!-- Row with Quality Overview & Alerts Filter -->
    <b-row class="match-height mb-1">
      <!-- 1. Qualidade da Telemetria (Cobertura & Amostras) -->
      <b-col
        cols="12"
        lg="5"
        class="mb-1 mb-lg-0"
      >
        <b-card
          no-body
          class="h-100 shadow-sm border-0 p-1"
        >
          <div class="d-flex align-items-center justify-content-between mb-1 pb-50 border-bottom">
            <div class="d-flex align-items-center">
              <feather-icon
                icon="ShieldCheckIcon"
                size="18"
                class="text-primary mr-50"
              />
              <h6 class="mb-0 font-weight-bolder text-dark">
                {{ $t('Data Quality & Coverage') }}
              </h6>
            </div>
            <b-badge
              v-if="quality"
              :variant="quality.coveragePercent >= 90 ? 'light-success' : 'light-danger'"
              pill
              class="font-small-1 font-weight-bolder"
            >
              {{ quality.coveragePercent >= 90 ? $t('High Quality') : $t('Coverage Warning') }}
            </b-badge>
          </div>

          <div
            v-if="quality"
            class="d-flex flex-column justify-content-between flex-grow-1"
          >
            <!-- Coverage Big Metric -->
            <div class="d-flex align-items-center justify-content-between p-75 rounded bg-light-primary border border-primary mb-1">
              <div>
                <span class="text-muted font-small-1 font-weight-bold text-uppercase d-block">
                  {{ $t('Payload Time Coverage') }}
                </span>
                <h3 class="font-weight-bolder text-primary mb-0 mt-25">
                  {{ formatNumber(quality.coveragePercent) }}%
                </h3>
                <small class="text-muted font-small-1">{{ $t('Gaps > 10 min are excluded from calculation') }}</small>
              </div>
              <b-avatar
                :variant="quality.coveragePercent >= 90 ? 'light-success' : 'light-warning'"
                size="46px"
                class="shadow-xs"
              >
                <feather-icon
                  :icon="quality.coveragePercent >= 90 ? 'CheckCircleIcon' : 'AlertCircleIcon'"
                  size="22"
                />
              </b-avatar>
            </div>

            <!-- Quality Counters Grid -->
            <b-row class="font-small-2 mb-75">
              <b-col
                cols="6"
                class="mb-50"
              >
                <div class="p-50 rounded bg-light-secondary border">
                  <span class="text-muted font-small-1 d-block">{{ $t('Readings with Load:') }}</span>
                  <strong class="text-success font-weight-bolder">{{ formatInteger(quality.readingsWithCrg) }}</strong>
                </div>
              </b-col>
              <b-col
                cols="6"
                class="mb-50"
              >
                <div class="p-50 rounded bg-light-secondary border">
                  <span class="text-muted font-small-1 d-block">{{ $t('Readings without Load:') }}</span>
                  <strong class="text-secondary font-weight-bolder">{{ formatInteger(quality.readingsWithoutCrg) }}</strong>
                </div>
              </b-col>
              <b-col cols="6">
                <div
                  v-b-tooltip.hover
                  class="p-50 rounded bg-light-secondary border cursor-pointer"
                  :title="$t('Incomplete cycle: the period cut the trip, there was a long gap without reading or unloading did not occur')"
                >
                  <span class="text-muted font-small-1 d-block">{{ $t('Incomplete Cycles:') }}</span>
                  <strong :class="quality.incompleteCycles > 0 ? 'text-danger font-weight-bolder' : 'text-dark'">
                    {{ quality.incompleteCycles }}
                  </strong>
                </div>
              </b-col>
              <b-col cols="6">
                <div class="p-50 rounded bg-light-secondary border">
                  <span class="text-muted font-small-1 d-block">{{ $t('Cycles with GPS Loss:') }}</span>
                  <strong :class="quality.gpsLossCycles > 0 ? 'text-warning font-weight-bolder' : 'text-dark'">
                    {{ quality.gpsLossCycles }}
                  </strong>
                </div>
              </b-col>
            </b-row>

            <!-- Quality Flags / Badges -->
            <div
              class="pt-50 border-top d-flex flex-wrap"
              style="gap: 6px;"
            >
              <b-badge
                v-if="quality.lowCoverage"
                v-b-tooltip.hover
                variant="light-danger"
                pill
                class="font-small-1"
                :title="$t('Payload telemetry coverage below 90%')"
              >
                <feather-icon
                  icon="AlertTriangleIcon"
                  size="11"
                  class="mr-25"
                />
                {{ $t('Low Coverage (\u003c90%)') }}
              </b-badge>
              <b-badge
                v-if="quality.standbyOrOffline"
                v-b-tooltip.hover
                variant="light-warning"
                pill
                class="font-small-1"
                :title="$t('Interval greater than 5 minutes without payload record')"
              >
                <feather-icon
                  icon="ClockIcon"
                  size="11"
                  class="mr-25"
                />
                {{ $t('Standby / No Load Detected') }}
              </b-badge>
              <b-badge
                v-if="quality.inconsistentHodometer"
                v-b-tooltip.hover
                variant="light-danger"
                pill
                class="font-small-1"
                :title="$t('Odometer dropped or reset during operation')"
              >
                <feather-icon
                  icon="SlashIcon"
                  size="11"
                  class="mr-25"
                />
                {{ $t('Inconsistent odometer') }}
              </b-badge>
              <b-badge
                v-if="!quality.lowCoverage && !quality.standbyOrOffline && !quality.inconsistentHodometer"
                variant="light-success"
                pill
                class="font-small-1"
              >
                <feather-icon
                  icon="CheckIcon"
                  size="11"
                  class="mr-25"
                />
                {{ $t('Stable Sensors') }}
              </b-badge>
            </div>
          </div>

          <div
            v-else
            class="text-center py-3 text-muted"
          >
            <span class="font-small-2">{{ $t('Loading quality metrics...') }}</span>
          </div>
        </b-card>
      </b-col>

      <!-- 2. Alertas Operacionais Detectados -->
      <b-col
        cols="12"
        lg="7"
      >
        <b-card
          no-body
          class="h-100 shadow-sm border-0 p-1"
        >
          <div
            class="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mb-1 pb-50 border-bottom"
            style="gap: 8px;"
          >
            <div class="d-flex align-items-center">
              <feather-icon
                icon="BellIcon"
                size="18"
                class="text-warning mr-50"
              />
              <div>
                <h6 class="mb-0 font-weight-bolder text-dark">
                  {{ $t('Operational Alerts & Sensors') }}
                </h6>
                <small class="text-muted">{{ $t('{count} alert(s) listed', { count: filteredAlerts.length }) }}</small>
              </div>
            </div>

            <!-- Severity Filter Buttons -->
            <b-button-group
              size="sm"
              class="flex-wrap"
            >
              <b-button
                :variant="severityFilter === 'all' ? 'primary' : 'outline-primary'"
                class="mb-25"
                @click="severityFilter = 'all'"
              >
                {{ $t('All') }} ({{ alerts.length }})
              </b-button>
              <b-button
                :variant="severityFilter === 'critical' ? 'danger' : 'outline-danger'"
                class="mb-25"
                @click="severityFilter = 'critical'"
              >
                {{ $t('Critical') }} ({{ countBySeverity('critical') }})
              </b-button>
              <b-button
                :variant="severityFilter === 'warning' ? 'warning' : 'outline-warning'"
                class="mb-25"
                @click="severityFilter = 'warning'"
              >
                {{ $t('Attention') }} ({{ countBySeverity('warning') }})
              </b-button>
              <b-button
                :variant="severityFilter === 'info' ? 'info' : 'outline-info'"
                class="mb-25"
                @click="severityFilter = 'info'"
              >
                {{ $t('Informative') }} ({{ countBySeverity('info') }})
              </b-button>
            </b-button-group>
          </div>

          <!-- Alerts List Container -->
          <div
            v-if="filteredAlerts.length > 0"
            class="alerts-list-scroll pr-25"
            style="max-height: 290px; overflow-y: auto;"
          >
            <div
              v-for="(alert, aIdx) in filteredAlerts"
              :key="`alert-${aIdx}`"
              class="alert-item p-75 mb-50 rounded border d-flex align-items-start justify-content-between"
              :class="resolveAlertBorderClass(alert.severity)"
            >
              <div class="d-flex align-items-start">
                <b-avatar
                  :variant="resolveAlertBadgeVariant(alert.severity)"
                  size="32px"
                  class="mr-75 flex-shrink-0 mt-25"
                >
                  <feather-icon
                    :icon="resolveAlertIcon(alert.code, alert.severity)"
                    size="16"
                  />
                </b-avatar>
                <div>
                  <div
                    class="d-flex align-items-center flex-wrap mb-25"
                    style="gap: 6px;"
                  >
                    <b-badge
                      :variant="resolveAlertBadgeVariant(alert.severity)"
                      pill
                      class="font-small-1 font-weight-bolder text-uppercase"
                    >
                      {{ resolveSeverityLabel(alert.severity) }}
                    </b-badge>
                    <b-badge
                      v-b-tooltip.hover
                      variant="light-secondary"
                      pill
                      class="font-small-1 font-weight-bold"
                      :title="alert.code ? $t('Technical code: {code}', { code: alert.code }) : ''"
                    >
                      {{ resolveAlertCodeLabel(alert.code) }}
                    </b-badge>
                    <b-badge
                      v-if="alert.cycleNumber !== null && alert.cycleNumber !== undefined"
                      v-b-tooltip.hover
                      variant="light-primary"
                      pill
                      class="font-small-1 font-weight-bold cursor-pointer"
                      :title="$t('Click to open cycle details')"
                      @click="$emit('select-cycle', alert.cycleNumber)"
                    >
                      <feather-icon
                        icon="EyeIcon"
                        size="10"
                        class="mr-25"
                      />
                      {{ $t('Cycle #{number}', { number: alert.cycleNumber }) }}
                    </b-badge>
                    <span
                      v-else
                      class="text-muted font-small-1"
                    >
                      {{ $t('(Period Alert)') }}
                    </span>
                  </div>
                  <p class="mb-0 font-small-2 text-dark font-weight-bold">
                    {{ resolveAlertMessage(alert) }}
                  </p>
                </div>
              </div>

              <b-button
                v-if="alert.cycleNumber !== null && alert.cycleNumber !== undefined"
                v-b-tooltip.hover
                variant="flat-primary"
                size="sm"
                class="btn-icon rounded-circle flex-shrink-0 ml-50"
                :title="$t('View Cycle')"
                @click="$emit('select-cycle', alert.cycleNumber)"
              >
                <feather-icon
                  icon="ChevronRightIcon"
                  size="16"
                />
              </b-button>
            </div>
          </div>

          <!-- Empty State for Alerts -->
          <div
            v-else
            class="text-center py-4 text-muted"
          >
            <b-avatar
              variant="light-success"
              size="44px"
              class="mb-50"
            >
              <feather-icon
                icon="CheckCircleIcon"
                size="24"
                class="text-success"
              />
            </b-avatar>
            <h6 class="font-weight-bold mb-25 text-dark">
              {{ $t('No alerts for the selected filter') }}
            </h6>
            <p class="font-small-3 mb-0">
              {{ $t('No operational nonconformities or anomalies were reported.') }}
            </p>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { ref, computed } from '@vue/composition-api'
import {
  BRow,
  BCol,
  BCard,
  BBadge,
  BAvatar,
  BButton,
  BButtonGroup,
  VBTooltip,
} from 'bootstrap-vue'
import { getIntlLocale } from '@/libs/i18n/dateLocale'

export default {
  name: 'QualityAlertsCard',
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BRow,
    BCol,
    BCard,
    BBadge,
    BAvatar,
    BButton,
    BButtonGroup,
  },
  props: {
    quality: {
      type: Object,
      default: null,
    },
    alerts: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { root }) {
    const severityFilter = ref('all')

    const filteredAlerts = computed(() => {
      if (!props.alerts) return []
      if (severityFilter.value === 'all') return props.alerts
      return props.alerts.filter(a => String(a.severity || '').toLowerCase() === severityFilter.value)
    })

    const countBySeverity = sev => {
      if (!props.alerts) return 0
      return props.alerts.filter(a => String(a.severity || '').toLowerCase() === String(sev).toLowerCase()).length
    }

    const formatNumber = val => {
      if (val === null || val === undefined || Number.isNaN(val)) return '—'
      return Number(val).toLocaleString(getIntlLocale(), {
        minimumFractionDigits: 1,
        maximumFractionDigits: 2,
      })
    }

    const formatInteger = val => {
      if (val === null || val === undefined || Number.isNaN(val)) return '0'
      return Number(val).toLocaleString(getIntlLocale())
    }

    const resolveSeverityLabel = sev => {
      const s = String(sev || '').toLowerCase()
      if (s === 'critical') return root.$t('Critical')
      if (s === 'warning') return root.$t('Attention')
      if (s === 'info') return root.$t('Informative')
      return sev || root.$t('Alert')
    }

    const resolveAlertBadgeVariant = sev => {
      const s = String(sev || '').toLowerCase()
      if (s === 'critical') return 'light-danger'
      if (s === 'warning') return 'light-warning'
      if (s === 'info') return 'light-info'
      return 'light-secondary'
    }

    const resolveAlertBorderClass = sev => {
      const s = String(sev || '').toLowerCase()
      if (s === 'critical') return 'bg-light-danger border-danger'
      if (s === 'warning') return 'bg-light-warning border-warning'
      if (s === 'info') return 'bg-light-info border-info'
      return 'bg-light-secondary border-secondary'
    }

    const alertCodeMap = {
      underload: 'Underload',
      overload: 'Overload',
      severeoverload: 'Severe Overload',
      overloadsequence: 'Overload Sequence',
      queue: 'Waiting in Transport',
      incompletecycle: 'Incomplete Cycle',
      incomplete: 'Incomplete Cycle',
      residue: 'Post-Unload Residue',
      residualpayload: 'Post-Unload Residue',
      scaledrift: 'Scale Drift',
      drift: 'Scale Drift',
      lowcoverage: 'Low Coverage',
      coverage: 'Low Coverage',
      gpsloss: 'GPS Loss',
      gpslost: 'GPS Loss',
      gps: 'GPS Loss',
      standby: 'Vehicle on Standby',
      standbyoroffline: 'Vehicle on Standby',
      inconsistentcounter: 'Inconsistent odometer',
      inconsistenthodometer: 'Inconsistent odometer',
      sensorcalibration: 'Sensor Calibration',
      calibration: 'Sensor Calibration',
      dynamicpeak: 'Dynamic Peak',
      dynamicpeaks: 'Dynamic Peaks',
    }

    const resolveAlertCodeLabel = code => {
      if (!code) return root.$t('Alert')
      const key = String(code).toLowerCase().replace(/[-_\s]/g, '')
      if (alertCodeMap[key]) return root.$t(alertCodeMap[key])

      const spaced = String(code)
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[-_]/g, ' ')
        .trim()
      return spaced.charAt(0).toUpperCase() + spaced.slice(1)
    }

    const resolveAlertMessage = alert => {
      if (!alert || !alert.message) return ''
      const msg = alert.message
      const num = alert.cycleNumber !== null && alert.cycleNumber !== undefined ? `#${alert.cycleNumber}` : ''

      if (msg.includes('is underloaded')) return root.$t('Cycle {number} operating underloaded (<90%).', { number: num })
      if (msg.includes('is severely overloaded')) return root.$t('Cycle {number} operating in severe overload (>120%).', { number: num })
      if (msg.includes('is overloaded')) return root.$t('Cycle {number} operating overloaded (110% to 120%).', { number: num })
      if (msg.includes('was stopped for more than 20%')) return root.$t('Cycle {number} stopped for more than 20% of transport time (waiting or queue).', { number: num })
      if (msg.includes('is incomplete')) return root.$t('Cycle {number} incomplete: cut by the period or with a telemetry gap.', { number: num })
      if (msg.includes('Residue after unloading')) return root.$t('Residue after unloading on Cycle {number} above 5% of nominal capacity.', { number: num })
      if (msg.includes('Scale drift')) return root.$t('Scale drift: the empty truck tare / reading varied more than 3% of capacity between cycles.')
      if (msg.includes('3 or more consecutive cycles')) return root.$t('3 or more consecutive cycles recorded in overload.')
      if (msg.includes('crg coverage is below')) return root.$t('Load telemetry coverage below 90% of the selected period.')
      if (msg.includes('GPS loss longer')) return root.$t('GPS signal loss for more than 5 minutes.')
      if (msg.includes('Readings without crg')) return root.$t('Records without load data for more than 5 minutes (vehicle stopped/standby).')
      if (msg.includes('Odometer (hod) decreased')) return root.$t('Odometer inconsistency: the value went backwards or was reset during the period.')
      if (msg.includes('Recurring dynamic peak')) return root.$t('Frequent dynamic peaks detected across multiple cycles. Sensor calibration is recommended.')

      return msg
    }

    const resolveAlertIcon = (code, sev) => {
      const c = String(code || '').toLowerCase()
      if (c.includes('severeoverload') || c.includes('overloadsequence')) return 'AlertOctagonIcon'
      if (c.includes('overload')) return 'AlertTriangleIcon'
      if (c.includes('underload')) return 'ArrowDownIcon'
      if (c.includes('queue')) return 'ClockIcon'
      if (c.includes('incomplete')) return 'XCircleIcon'
      if (c.includes('residue') || c.includes('scaledrift')) return 'ScaleIcon'
      if (c.includes('gps')) return 'MapPinIcon'
      if (c.includes('coverage') || c.includes('standby')) return 'WifiOffIcon'
      if (c.includes('counter')) return 'HashIcon'
      if (c.includes('calibration')) return 'SlidersIcon'

      const s = String(sev || '').toLowerCase()
      if (s === 'critical') return 'AlertOctagonIcon'
      if (s === 'warning') return 'AlertTriangleIcon'
      return 'InfoIcon'
    }

    return {
      severityFilter,
      filteredAlerts,
      countBySeverity,
      formatNumber,
      formatInteger,
      resolveSeverityLabel,
      resolveAlertBadgeVariant,
      resolveAlertBorderClass,
      resolveAlertCodeLabel,
      resolveAlertMessage,
      resolveAlertIcon,
    }
  },
}
</script>

<style scoped>
.alerts-list-scroll::-webkit-scrollbar {
  width: 5px;
}
.alerts-list-scroll::-webkit-scrollbar-thumb {
  background: #d8d6de;
  border-radius: 4px;
}
</style>
